class InteractiveSlideshow extends HTMLElement { 
  constructor() {
    super();

    this.section = this.closest('.shopify-section');
    this.layoutSection = this.closest('.layout-section');
    this.overlayElement = this.querySelector('.interactive-slideshow-overlay');
    
    if (this.dataset.animation !== "true") {
      this.layoutSection && (this.layoutSection.style.minHeight = '');
      return;
    }
    
    this.maxWidth = 35;
    this.animationDistance = 0;
    this.startBufferRatio = 0.1;
    this.endBufferRatio = 0.1;
    
    // Animation state
    this.currentWidth = 0;
    this.targetWidth = 0;
    
    // Cached values to avoid repeated DOM reads
    this.cachedHeaderHeight = 0;
    this.cachedAnnouncementHeight = 0;
    this.cachedLayoutHeight = 0;
    
    // RAF state
    this.rafId = null;
    this.active = false;
    this.frameScheduled = false;
    
    // Throttled/batched handlers
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = this.debounceResize.bind(this);
    this.animate = this.animate.bind(this);
    
    // Media query
    this.mq = window.matchMedia('(min-width: 1025px)');
    this.handleOrientationChange = this.debounceOrientation.bind(this);
    
    // Performance optimization flags
    this.needsBoundsUpdate = true;
    this.lastScrollY = 0;
  }

  connectedCallback() {
    if (this.dataset.animation !== "true") return;
    
    // Schedule initial measurements for next frame
    requestAnimationFrame(() => {
      this.cacheInitialMeasurements();
    });
    
    // Media query setup
    if (this.mq.addEventListener) {
      this.mq.addEventListener("change", this.mqListener);
    }
    
    // Setup orientation/resize listeners
    window.addEventListener('orientationchange', this.handleOrientationChange);
    if (Shopify.designMode) {
      window.addEventListener('resize', this.handleOrientationChange);
    }
    
    // Initial activation state
    requestAnimationFrame(() => {
      this.mq.matches ? this.activate() : this.deactivate();
    });
  }
  
  disconnectedCallback() {
    if (this.mq && this.mq.removeEventListener) {
      this.mq.removeEventListener("change", this.mqListener);
    }
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    this.deactivate();
  }
  
  cacheInitialMeasurements() {
    // Cache all measurements in one synchronous batch
    if (this.layoutSection) {
      this.cachedLayoutHeight = this.layoutSection.offsetHeight;
      this.layoutSection.dataset.baseHeight = this.cachedLayoutHeight;
    }
    
    // Cache header and announcement heights
    const header = document.querySelector('header');
    const announcement = document.querySelector('announcement-bar');
    this.cachedHeaderHeight = header ? header.offsetHeight : 0;
    this.cachedAnnouncementHeight = announcement ? announcement.offsetHeight : 0;
    
    this.needsBoundsUpdate = true;
  }
  
  debounceResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.needsBoundsUpdate = true;
      this.cacheInitialMeasurements();
    }, 250);
  }
  
  debounceOrientation() {
    if (this.orientationTimeout) clearTimeout(this.orientationTimeout);
    this.orientationTimeout = setTimeout(() => {
      this.cacheInitialMeasurements();
      this.mq.matches ? this.activate() : this.deactivate();
    }, 150);
  }
  
  activate() {
    if (this.active) return;
    this.active = true;
    
    // Update bounds if needed
    if (this.needsBoundsUpdate) {
      this.updateBounds();
    }
    
    // Setup listeners with passive flags
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });
    
    // Start animation loop
    this.startAnimation();
  }
  
  deactivate() {
    if (!this.active) {
      this.resetStyles();
      return;
    }
    
    this.active = false;
    
    // Remove listeners
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
    
    // Stop animation
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    
    // Reset state
    this.currentWidth = 0;
    this.targetWidth = 0;
    this.resetStyles();
  }
  
  resetStyles() {
    // Batch all style resets together
    requestAnimationFrame(() => {
      this.setOverlayWidth(0);
      if (this.overlayElement) {
        this.overlayElement.classList.remove('active');
      }
      if (this.layoutSection && this.layoutSection.dataset.baseHeight) {
        this.layoutSection.style.minHeight = '';
      }
    });
  }
  
  updateBounds() {
    if (!this.section || !this.active) return;
    
    // Batch all DOM reads at once
    const rect = this.section.getBoundingClientRect();
    this.sectionTop = rect.top + window.scrollY;
    this.animationDistance = window.innerHeight;
    
    // Calculate and apply layout changes in one batch
    if (this.overlayElement && this.layoutSection && this.cachedLayoutHeight) {
      const totalHeight = this.cachedLayoutHeight + this.animationDistance;
      
      // Apply all style changes together
      requestAnimationFrame(() => {
        this.layoutSection.style.minHeight = `${totalHeight}px`;
      });
    }
    
    this.needsBoundsUpdate = false;
  }
  
  handleScroll() {
    if (!this.active || !this.section || !this.overlayElement) return;
    
    const scrollY = window.scrollY;
    
    // Throttle scroll calculations
    if (Math.abs(scrollY - this.lastScrollY) < 1) return;
    this.lastScrollY = scrollY;
    
    const start = this.sectionTop;
    const end = start + this.animationDistance;
    
    const startBuffer = this.animationDistance * this.startBufferRatio;
    const endBuffer = this.animationDistance * this.endBufferRatio;
    
    // Avoid buffer overlap
    let sBuf = startBuffer;
    let eBuf = endBuffer;
    if (sBuf + eBuf >= this.animationDistance) {
      sBuf = 0;
      eBuf = 0;
    }
    
    const animStart = start + sBuf;
    const animEnd = end - eBuf;
    
    // Calculate target width
    let targetWidth;
    if (scrollY <= animStart) {
      targetWidth = 0;
    } else if (scrollY >= animEnd) {
      targetWidth = this.maxWidth;
    } else {
      const progress = (scrollY - animStart) / (animEnd - animStart);
      targetWidth = this.maxWidth * progress;
    }
    
    // Update target width (this will be picked up by animation loop)
    this.targetWidth = targetWidth;
    
    // Schedule animation frame if not already scheduled
    if (!this.frameScheduled) {
      this.frameScheduled = true;
      requestAnimationFrame(() => {
        this.animate();
        this.frameScheduled = false;
      });
    }
  }
  
  startAnimation() {
    const animateLoop = () => {
      if (!this.active) {
        this.rafId = null;
        return;
      }
      
      this.animate();
      this.rafId = requestAnimationFrame(animateLoop);
    };
    
    this.rafId = requestAnimationFrame(animateLoop);
  }
  
  animate() {
    // Smoothing with damping
    const smoothing = 0.8;
    this.currentWidth += (this.targetWidth - this.currentWidth) * smoothing;
    
    // Apply updates in batch
    requestAnimationFrame(() => {
      this.setOverlayWidth(this.currentWidth);
      
      // Only toggle class when crossing threshold
      const threshold = 30; 
      if (this.currentWidth >= threshold && !this.overlayElement.classList.contains('active')) {
        this.overlayElement.classList.add('active');
      } else if (this.currentWidth < threshold && this.overlayElement.classList.contains('active')) {
        this.overlayElement.classList.remove('active');
      }
    });
  }
  
  setOverlayWidth(width) {
    // Batch CSS custom property updates
    this.style.setProperty('--slider-width', `${width}%`);
    if (this.overlayElement) {
      this.overlayElement.style.setProperty('--slideshow-overlay--width', `${width}%`);
    }
  }
}

if (!customElements.get('interactive-slideshow')) {
  customElements.define('interactive-slideshow', InteractiveSlideshow);
}